import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, Calendar, Check } from "lucide-react";
import Seo from "../../components/Seo";
import { businessLd, breadcrumbLd, serviceLd } from "../../components/StructuredData";
import { services } from "../../data/services";

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const idx = services.findIndex((s) => s.slug === slug);
  if (idx === -1) return <Navigate to="/services" replace />;

  const service = services[idx];
  const next = services[idx + 1] || null;
  const prev = services[idx - 1] || null;

  const jsonLd = [
    businessLd(),
    serviceLd({
      name: service.title,
      description: service.description,
      slug: service.slug,
      serviceType: service.serviceType,
    }),
    breadcrumbLd([
      { name: "Home", path: "/" },
      { name: "Services", path: "/services" },
      { name: service.title, path: `/services/${service.slug}` },
    ]),
  ];

  return (
    <>
      <Seo
        title={`${service.title} — ${service.headline}`}
        description={service.short}
        path={`/services/${service.slug}`}
        image={service.image}
        jsonLd={jsonLd}
      />
      <div className="min-h-screen bg-cream-50 pt-20 sm:pt-24">
        <div className="relative h-72 sm:h-96 overflow-hidden">
          <img
            src={service.image}
            alt={`${service.title} — ${service.headline}`}
            className="w-full h-full object-cover"
            loading="eager"
            fetchpriority="high"
            decoding="async"
            width="1200"
            height="700"
          />
          <div className="absolute inset-0 bg-matte-900/60 flex items-center justify-center">
            <div className="text-center max-w-3xl px-4">
              <p className="font-sans text-xs tracking-[0.4em] text-walnut-300 uppercase mb-3">
                Stage {service.stage} of 03
              </p>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white font-light leading-tight mb-3">
                {service.title}
              </h1>
              <p className="font-sans text-sm sm:text-base text-cream-300">
                {service.headline}
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-8 py-14 sm:py-20">
          <div className="flex items-center gap-2 font-sans text-xs text-matte-400 mb-8">
            <Link to="/" className="hover:text-walnut-500 transition-colors">Home</Link>
            <span>/</span>
            <Link to="/services" className="hover:text-walnut-500 transition-colors">Services</Link>
            <span>/</span>
            <span className="text-matte-700">{service.title}</span>
          </div>

          <p className="font-serif text-xl sm:text-2xl text-matte-700 italic leading-relaxed mb-10">
            {service.short}
          </p>
          <p className="font-sans text-base text-matte-500 leading-relaxed mb-12">
            {service.description}
          </p>

          <div className="bg-white border border-cream-200 rounded-sm p-8 mb-12">
            <h2 className="font-serif text-2xl text-matte-900 font-light mb-6">
              What you get
            </h2>
            <ul className="space-y-3">
              {service.deliverables.map((d) => (
                <li
                  key={d}
                  className="flex gap-3 items-start font-sans text-sm sm:text-base text-matte-700"
                >
                  <Check size={18} className="text-walnut-500 mt-0.5 flex-shrink-0" />
                  {d}
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-matte-900 text-cream-200 rounded-sm p-8 mb-12">
            <p className="font-sans text-xs tracking-widest text-walnut-300 uppercase mb-3">
              Timing
            </p>
            <p className="font-serif text-xl leading-relaxed">{service.when}</p>
          </div>

          <div className="text-center">
            <Link
              to="/book"
              className="inline-flex items-center gap-2 bg-walnut-500 hover:bg-walnut-600 text-white font-sans text-sm tracking-wider px-10 py-4 transition-colors"
            >
              <Calendar size={16} />
              {service.cta}
            </Link>
          </div>
        </div>

        <div className="border-t border-cream-200 bg-cream-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-6">
            {prev ? (
              <Link
                to={`/services/${prev.slug}`}
                className="font-sans text-sm text-matte-500 hover:text-walnut-600 transition-colors flex items-center gap-2"
              >
                <ArrowRight size={14} className="rotate-180" />
                <span>
                  <span className="block text-xs text-matte-400 uppercase tracking-widest">
                    Stage {prev.stage}
                  </span>
                  {prev.title}
                </span>
              </Link>
            ) : (
              <span />
            )}
            {next ? (
              <Link
                to={`/services/${next.slug}`}
                className="font-sans text-sm text-matte-500 hover:text-walnut-600 transition-colors flex items-center gap-2 sm:text-right"
              >
                <span>
                  <span className="block text-xs text-matte-400 uppercase tracking-widest">
                    Stage {next.stage}
                  </span>
                  {next.title}
                </span>
                <ArrowRight size={14} />
              </Link>
            ) : (
              <Link
                to="/portfolio"
                className="font-sans text-sm text-matte-500 hover:text-walnut-600 transition-colors flex items-center gap-2"
              >
                Browse the Portfolio
                <ArrowRight size={14} />
              </Link>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
